import { useMemo } from "react"
import { type TMultiSelectOption } from "~/ui-rtk/components/ui/controls/MultiSelect"
import useConnect from "~/ui-rtk/components/ui/charts/ChartDataLoader/connect"
import { useMinMaxDateRange } from "~/ui-rtk/hooks/date-range"
import { usePeriod } from "~/ui-rtk/hooks/period"

const CUBE = "company_web_analytics_page_path_report"
const HANDLE_KEY = `${CUBE}.handle`
const TITLE_KEY = `${CUBE}.title`
const REVENUE_KEY = `${CUBE}.total_revenue`

/**
 * Fetches the distinct Shopify product handles (with titles) from the page path
 * report, ranked by total revenue (highest first), for the product filter on the
 * creative / ads reports.
 *
 * The option `value` is the SENTINEL-WRAPPED handle (`,<handle>,`) so it matches
 * the `*_product_handles_str` proxy dimensions on the ads / creative cubes via a
 * `contains` operator (whole-element membership, no substring false positives).
 * The `label` is the human-readable product title (falling back to the handle).
 */
export function useProductHandleOptions() {
  const { dateRange } = useMinMaxDateRange()
  const { granularity } = usePeriod()

  const { dataSource, isLoading } = useConnect({
    chartId: "product_handle_options_query",
    granularity,
    dateRange: dateRange || [new Date(), new Date()],
    page: 0,
    itemsPerPage: 10000,
    enablePagination: false,
  })

  const productOptions = useMemo(() => {
    if (!dataSource) return []

    // Aggregate revenue per handle (defensive — the cube query already groups by
    // handle) then sort by revenue descending so the most relevant products
    // surface at the top of the dropdown.
    const revenueByHandle = new Map<string, number>()
    const titleByHandle = new Map<string, string>()
    dataSource.forEach(row => {
      const handle = row[HANDLE_KEY]
      if (handle === null || handle === undefined || handle === "") return
      const key = String(handle)
      const revenue = Number(row[REVENUE_KEY] ?? 0)
      revenueByHandle.set(key, (revenueByHandle.get(key) ?? 0) + revenue)
      const title = row[TITLE_KEY]
      if (!titleByHandle.has(key) && title) {
        titleByHandle.set(key, String(title))
      }
    })

    return Array.from(revenueByHandle.entries())
      .sort(([, a], [, b]) => b - a)
      .map(([handle]) => ({
        // Sentinel-wrapped value matches the proxy dimension `contains` filter.
        value: `,${handle},`,
        label: titleByHandle.get(handle) || handle,
      })) as TMultiSelectOption[]
  }, [dataSource])

  return {
    productOptions,
    isLoading,
  }
}

export default useProductHandleOptions
